// 채팅의 **표시 방식**(설계서 §9.6.6) — 옆에 고정(dock) / 별도 창(popup).
//
// 왜 둘 다 필요한가(사용자 요구, 2026-09-23):
//  - **고정**: 대화를 계속 주고받는 동안 원격 화면과 나란히 본다. 화면이 가려지지 않는 대신 원격이 좁아진다.
//  - **별도 창**: 원격 화면을 **넓게 쓰면서** 대화 창은 바탕화면 아무 곳(다른 모니터 포함)에 둔다.
//
// 📌 2026-09-24 변경: 종전 `popup` 은 세션 창 **안**에 떠 있는 패널이라 원격 화면 영역 밖으로 옮길 수 없었다
//    (Mac 실기 사용자 보고). 이제 `popup` = **독립 OS 창**(`useChatWindow`). 위치·크기는 OS 창이 맡으므로
//    여기서 좌표를 들고 있지 않는다(종전의 `rs_chat_popup_pos` 는 더 쓰지 않는다).
//
// 🔴 고정일 때는 **무대가 패널만큼 줄어들어야** 한다 — 패널은 `position: absolute` 라 그냥 두면 영상을 **덮는다**
//    (2026-09-23 사용자 보고). 무대에 `chat-dock` 을 붙여 오른쪽 여백을 준다.
//
// 설정은 **뷰어 로컬**에 남긴다(화질 기본값과 같은 선 — 서버·DB 를 건드리지 않는다).
import { useCallback, useState } from 'react';

/** 표시 방식. 기본은 `dock`(종전 동작 유지). 저장 값 `popup` 은 이제 "별도 창" 을 뜻한다. */
export type ChatMode = 'dock' | 'popup';

const MODE_KEY = 'rs_chat_mode';
/** 고정 패널 폭(px) 저장 키 — 사람이 가장자리를 끌어 바꾼 폭을 다음 세션에도 쓴다(2026-09-24 사용자 요구). */
const DOCK_W_KEY = 'rs_chat_dock_w';

/** 고정 패널 폭의 기본·최소값(px). 최대는 무대 폭 비율([DOCK_W_MAX_RATIO])로 끄는 쪽에서 자른다. */
export const DOCK_W_DEFAULT = 340;
export const DOCK_W_MIN = 260;
/** 고정 패널이 무대에서 차지할 수 있는 최대 비율 — 원격 화면이 너무 좁아지지 않게. */
export const DOCK_W_MAX_RATIO = 0.7;

/** 폭을 [DOCK_W_MIN, maxPx] 로 자른다(maxPx 가 최소보다 작으면 최소). 정수 px. */
export function clampDockWidth(w: number, maxPx: number): number {
  const hi = Math.max(DOCK_W_MIN, maxPx);
  return Math.round(Math.min(Math.max(w, DOCK_W_MIN), hi));
}

function loadDockWidth(): number {
  try {
    const v = Number(localStorage.getItem(DOCK_W_KEY));
    // 저장값이 없거나 이상하면 기본값. 상한은 화면에 따라 다르므로 여기서는 최소만 본다.
    return Number.isFinite(v) && v >= DOCK_W_MIN ? Math.round(v) : DOCK_W_DEFAULT;
  } catch {
    return DOCK_W_DEFAULT;
  }
}

function loadMode(): ChatMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'popup' ? 'popup' : 'dock';
  } catch {
    return 'dock'; // 저장소를 못 읽어도 화면은 떠야 한다
  }
}

export interface ChatLayout {
  mode: ChatMode;
  setMode(m: ChatMode): void;
  /** 고정 패널 폭(px). 무대의 여백과 패널 폭이 **같은 값**을 쓴다(`--chat-dock-w`). */
  dockWidth: number;
  /** 폭 바꾸기 — 끄는 동안에는 `persist=false`(매 움직임마다 저장하지 않는다), 놓을 때 `true`. */
  setDockWidth(w: number, persist: boolean): void;
}

export function useChatLayout(): ChatLayout {
  const [mode, setModeState] = useState<ChatMode>(loadMode);
  const [dockWidth, setDockWidthState] = useState<number>(loadDockWidth);

  const setDockWidth = useCallback((w: number, persist: boolean) => {
    setDockWidthState(w);
    if (persist) {
      try {
        localStorage.setItem(DOCK_W_KEY, String(w));
      } catch {
        // 저장 실패는 조용히(이번 세션에는 적용됐다)
      }
    }
  }, []);

  const setMode = useCallback((m: ChatMode) => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      // 저장 실패는 조용히 넘어간다(이번 세션에는 이미 적용됐다)
    }
  }, []);

  return { mode, setMode, dockWidth, setDockWidth };
}
